import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FiCheckCircle, FiAlertCircle, FiLock, FiLoader } from "react-icons/fi";

import { API_URL } from "../config";

function ResetPassword() {
	const { token } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isWelcome = searchParams.get("welcome") === "1";

	const [verifying, setVerifying] = useState(true);
	const [tokenValid, setTokenValid] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);

	// Verify the token when the page loads
	useEffect(() => {
		const verifyToken = async () => {
			try {
				await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
				setTokenValid(true);
			} catch (err) {
				setTokenValid(false);
				setError(err.response?.data?.error || "This reset link is invalid or has expired.");
			}
			setVerifying(false);
		};

		if (token) {
			verifyToken();
		} else {
			setVerifying(false);
			setError("No reset token provided.");
		}
	}, [token]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		if (newPassword !== confirmPassword) {
			setError("Passwords do not match");
			return;
		}

		if (newPassword.length < 8) {
			setError("Password must be at least 8 characters");
			return;
		}

		setLoading(true);

		try {
			const response = await axios.post(`${API_URL}/auth/confirm-reset`, {
				token,
				newPassword,
			});
			setSuccess(response.data.message);
		} catch (err) {
			setError(err.response?.data?.error || "Error resetting password");
		}

		setLoading(false);
	};

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="glass-strong p-6 sm:p-8 lg:p-10 rounded-3xl shadow-2xl w-full max-w-md">
				<div className="text-center mb-6 sm:mb-8">
					<h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3c79bb] mb-2 drop-shadow-lg">PW Connect</h1>
					<p className="text-gray-900 text-sm sm:text-base font-medium">Team Communication & Training</p>
				</div>

				{/* ── Verifying token ── */}
				{verifying && (
					<div className="text-center py-8">
						<FiLoader className="animate-spin text-3xl text-[#3c79bb] mx-auto mb-3" />
						<p className="text-gray-700 font-medium">
							{isWelcome ? "Verifying your setup link..." : "Verifying your reset link..."}
						</p>
					</div>
				)}

				{/* ── Invalid / expired token ── */}
				{!verifying && !tokenValid && !success && (
					<div className="text-center py-4">
						<div className="glass bg-red-100/50 border border-red-300/50 text-red-800 px-4 py-4 rounded-xl mb-5 backdrop-blur-md flex items-start gap-2">
							<FiAlertCircle className="text-xl flex-shrink-0 mt-0.5" />
							<div className="text-left">
								<p className="text-sm sm:text-base font-semibold">Link expired or invalid</p>
								<p className="text-xs sm:text-sm mt-1">{error}</p>
							</div>
						</div>
						<button
							onClick={() => navigate("/login")}
							className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 px-4 rounded-xl hover:bg-[#306196] transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 font-semibold text-sm sm:text-base"
						>
							Back to Login
						</button>
					</div>
				)}

				{/* ── Success ── */}
				{success && (
					<div className="text-center py-4">
						<div className="glass bg-green-100/50 border border-green-300/50 text-green-800 px-4 py-4 rounded-xl mb-5 backdrop-blur-md flex items-start gap-2">
							<FiCheckCircle className="text-xl flex-shrink-0 mt-0.5" />
							<div className="text-left">
								<p className="text-sm sm:text-base font-semibold">
									{isWelcome ? "You are all set!" : "Password reset successful!"}
								</p>
								<p className="text-xs sm:text-sm mt-1">{success}</p>
							</div>
						</div>
						<button
							onClick={() => navigate("/login")}
							className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 px-4 rounded-xl hover:bg-[#306196] transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 font-semibold text-sm sm:text-base"
						>
							Go to Login
						</button>
					</div>
				)}

				{/* ── New password form ── */}
				{!verifying && tokenValid && !success && (
					<>
						<h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
							{isWelcome ? "Create your password" : "Choose a new password"}
						</h2>
						<p className="text-xs sm:text-sm text-gray-700 font-medium mb-4 sm:mb-6">
							{isWelcome
								? "Choose a password for your new account (at least 8 characters)."
								: "Enter your new password below. It must be at least 8 characters."}
						</p>

						<form onSubmit={handleSubmit} className="space-y-4">
							{error && (
								<div className="glass bg-red-100/50 border border-red-300/50 text-red-800 px-4 py-3 rounded-xl backdrop-blur-md">
									<p className="text-sm sm:text-base">{error}</p>
								</div>
							)}

							<div>
								<label htmlFor="new-password" className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
									{isWelcome ? "Password" : "New password"}
								</label>
								<div className="relative">
									<FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
									<input
										id="new-password"
										type="password"
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										required
										minLength={8}
										className="w-full pl-10 pr-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium"
										placeholder="Minimum 8 characters"
									/>
								</div>
							</div>

							<div>
								<label htmlFor="confirm-password" className="block text-sm sm:text-base font-semibold text-gray-900 mb-2">
									{isWelcome ? "Confirm password" : "Confirm new password"}
								</label>
								<div className="relative">
									<FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
									<input
										id="confirm-password"
										type="password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										required
										minLength={8}
										className="w-full pl-10 pr-4 py-2.5 sm:py-3 glass border border-white/30 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent focus:glass-strong transition-all text-gray-900 placeholder-gray-600 font-medium"
										placeholder="Re-enter new password"
									/>
								</div>
							</div>

							<button
								type="submit"
								disabled={loading}
								className="w-full bg-[#3c79bb] text-white py-2.5 sm:py-3 px-4 rounded-xl hover:bg-[#306196] focus:outline-none focus:ring-2 focus:ring-[#3c79bb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold text-sm sm:text-base"
							>
								{loading ? (isWelcome ? "Saving..." : "Resetting...") : isWelcome ? "Save password" : "Reset password"}
							</button>
						</form>
					</>
				)}
			</div>
		</div>
	);
}

export default ResetPassword;
